import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Autocomplete, Button, CircularProgress, Divider, OutlinedInput, Stack } from "@mui/material";
import * as React from "react";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { ingestService, setupService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { ConfigPackNameDto } from "../../../Core/Api/Types/db-ingest-config-pack-infos";
import { PlaceholderNameDto } from "../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-infos";
import { ProviderNewAssignmentDto } from "../../../Core/Api/Types/db-lab-provider-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { GetObject } from "../../../Redux/Reducers/redux-slice-asset";
import { useAppSelector } from "../../../Redux/redux-store";
import { UserHasAcls } from "../../../Utils/helpers";
import MoveAssetTreeView, { MoveAssetTreeViewProps } from "../../TreeView/MoveAssetTreeView/move-asset-tree-view";
import { useTranslation } from "react-i18next";
import { GetSelectedItems, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";

export type ModalCopyAssetElementProps = {
  selectableObjectLayers?: ObjectLayer[];
  objectIdsToDisable: number[];
  objectIdsToHilight: number[];
  onCopyAssetClick: (
    selectedParentId: number,
    selectedProviderId: number,
    successCallback: () => void,
    errorCallback: () => void,
    selectedPlaceholderId?: number
  ) => void;
};

export type ModalCopyAssetProps = CommonModalProps<ModalCopyAssetElementProps>;

const ModalCopyAsset = forwardRef<any, ModalCopyAssetProps>((props, ref) => {
  const { closeModal, ...elementProps } = props;
  const { selectableObjectLayers, objectIdsToDisable, objectIdsToHilight, onCopyAssetClick } = elementProps;

  const { t } = useTranslation();

  // tree
  const currentObject = useAppSelector(GetObject);
  const selectedItems = useAppSelector(GetSelectedItems(TreeContext.MoveDistrib));
  const [titleId, setTitleId] = useState<number | null>();
  const [showLoader, setShowLoader] = useState(false);

  // form
  const userHasRightToModifyPlaceholder = useMemo(
    () => UserHasAcls([AclTypes.aclIngestPlaceholderCreate, AclTypes.aclCopyObjectOverridingPlaceholderTemplate]),
    []
  );
  // options
  const [providersDto, setProvidersDto] = useState<ProviderNewAssignmentDto[]>([]);
  const [packsDto, setPacksDto] = useState<ConfigPackNameDto[]>([]);
  const [placeholdersDto, setPlaceHoldersDto] = useState<PlaceholderNameDto[]>([]);
  // selections
  const [selectedProvider, setSelectedProvider] = useState<ProviderNewAssignmentDto | null>(null);
  const [selectedPack, setSelectedPack] = useState<ConfigPackNameDto | null>(null);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState<PlaceholderNameDto | null>(null);

  const handleCopyAsset = useCallback(() => {
    if (selectedItems && selectedProvider) {
      setShowLoader(true);
      onCopyAssetClick(
        selectedItems[0],
        selectedProvider.id,
        closeModal,
        () => setShowLoader(false),
        selectedPlaceholder?.id
      );
    }
  }, [selectedItems, selectedProvider, onCopyAssetClick]);

  useEffect(() => {
    setupService.GetProvidersDto().then((providersDto) => {
      setProvidersDto(providersDto.sort((a, b) => a.name.localeCompare(b.name)));
    });
    if (userHasRightToModifyPlaceholder) {
      ingestService.GetConfigPackNameDtoList().then((configPacksDto) => {
        setPacksDto(configPacksDto.sort((a, b) => a.name.localeCompare(b.name)));
      });
    }
  }, []);

  useEffect(() => {
    setSelectedPlaceholder(null);

    if (selectedPack === null) {
      setPlaceHoldersDto([]);
      return;
    }

    ingestService.GetPlaceholderNameDtoList(selectedPack.id).then((placeholdersDto) => {
      setPlaceHoldersDto(placeholdersDto.sort((a, b) => a.name.localeCompare(b.name)));
    });
  }, [selectedPack]);

  useEffect(() => {
    if (!currentObject) return;

    if (currentObject.layer === ObjectLayer.Title) {
      setTitleId(currentObject.id);
    } else {
      setTitleId(currentObject.parentTitleId);
    }
  }, [currentObject]);

  return (
    <Modal
      title={t("modal:title.copyAsset")}
      reference={ref}
      closeModal={closeModal}
      width={504}
      height={900} // Todo: Might need to delete later
      footer={
        <Button
          variant="contained"
          color="primary"
          onClick={handleCopyAsset}
          startIcon={!showLoader && <ContentCopyOutlinedIcon />}
          fullWidth
          disabled={selectedItems.length === 0 || !selectedProvider || showLoader}
        >
          {showLoader ? <CircularProgress size={20} thickness={5} color="secondary" /> : t("button:copyAssets")}{" "}
        </Button>
      }
    >
      <Stack spacing={1}>
        <Autocomplete
          popupIcon={<ExpandMoreRoundedIcon />}
          options={providersDto}
          getOptionLabel={(option) => option.name}
          value={selectedProvider}
          onChange={(e, value) => setSelectedProvider(value)}
          renderInput={(params) => (
            <OutlinedInput
              {...params.InputProps}
              inputProps={params.inputProps}
              placeholder={t("field:placeholder.selectProvider")}
              sx={{ width: "100%" }}
            />
          )}
          sx={{ width: "100%" }}
        />
        {userHasRightToModifyPlaceholder && (
          <>
            <Stack direction="row" spacing={1} flex={1}>
              <Autocomplete
                popupIcon={<ExpandMoreRoundedIcon />}
                options={packsDto}
                getOptionLabel={(option) => option.name}
                value={selectedPack}
                onChange={(e, value) => setSelectedPack(value)}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("common:selectPack")}
                    sx={{ width: "100%" }}
                  />
                )}
                sx={{ width: "100%" }}
              />
              <Autocomplete
                popupIcon={<ExpandMoreRoundedIcon />}
                options={placeholdersDto}
                getOptionLabel={(option) => option.name}
                value={selectedPlaceholder}
                onChange={(e, value) => setSelectedPlaceholder(value)}
                disabled={placeholdersDto.length === 0}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.selectPlaceholder")}
                    disabled={placeholdersDto.length === 0}
                    sx={{ width: "100%" }}
                  />
                )}
                sx={{ width: "100%" }}
              />
            </Stack>
          </>
        )}
      </Stack>
      <Divider sx={{ mt: 2, mb: 2 }} />

      {titleId && (
        <MoveAssetTreeView
          treeRootId={titleId}
          selectableObjectLayers={selectableObjectLayers}
          objectsIdsToDisable={objectIdsToDisable}
          objectIdsToHilight={objectIdsToHilight}
        />
      )}
    </Modal>
  );
});

export default ModalCopyAsset;
